import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cx } from '@/lib/clases'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { useEstadoNomina } from '@/dominio/nomina/useNomina'
import { EncabezadoPantalla } from './-pantalla'
import { EmpresaNominaSeccion } from './-nomina/Empresa'
import { EmpleadosNomina } from './-nomina/Empleados'
import { ArchivosSeccion } from './-nomina/Archivos'
import { LiquidacionNomina, SimuladorNomina } from './-nomina/Herramientas'
import { PeriodosNomina } from './-nomina/Periodos'
import { PrestamosNomina } from './-nomina/Prestamos'
import { ResumenNomina } from './-nomina/Resumen'
import { ObligacionesNomina } from './-nomina/Obligaciones'
import { ReportesNomina } from './-nomina/Reportes'
import { ContabilidadNomina } from './-nomina/Contabilidad'
import { LicenciasNomina } from './-nomina/Licencias'
import { TiempoNomina } from './-nomina/Tiempo'
import { CajaNomina } from './-nomina/Caja'
import { SolicitudesPortalNomina } from './-nomina/AccesoPortal'

/**
 * Nómina (`docs/10-nomina.md`): el complemento que el dueño enciende por
 * contribuyente.
 *
 * Una pantalla con secciones y no siete rutas sueltas: es un solo trabajo que se
 * hace en orden —empresa, empleados, período, archivos— y la sección va en la URL
 * para poder pasar el enlace.
 */

const SECCIONES = [
  { id: 'resumen', etiqueta: 'Resumen' },
  { id: 'empleados', etiqueta: 'Empleados' },
  { id: 'periodos', etiqueta: 'Períodos' },
  { id: 'licencias', etiqueta: 'Licencias' },
  { id: 'asistencia', etiqueta: 'Asistencia' },
  { id: 'prestamos', etiqueta: 'Préstamos y descuentos' },
  { id: 'archivos', etiqueta: 'Archivos' },
  { id: 'obligaciones', etiqueta: 'Obligaciones' },
  { id: 'reportes', etiqueta: 'Reportes' },
  { id: 'contabilidad', etiqueta: 'Contabilidad' },
  { id: 'caja', etiqueta: 'Caja' },
  { id: 'liquidacion', etiqueta: 'Liquidación' },
  { id: 'simulador', etiqueta: 'Simulador' },
  { id: 'portal', etiqueta: 'Portal del empleado' },
  { id: 'empresa', etiqueta: 'Empresa' },
] as const

type IdSeccion = (typeof SECCIONES)[number]['id']

function esSeccion(valor: unknown): valor is IdSeccion {
  return SECCIONES.some((s) => s.id === valor)
}

export const Route = createFileRoute('/nomina')({
  validateSearch: (busqueda: Record<string, unknown>): { seccion?: IdSeccion } => ({
    seccion: esSeccion(busqueda.seccion) && busqueda.seccion !== 'resumen' ? busqueda.seccion : undefined,
  }),
  component: PantallaNomina,
})

function PantallaNomina() {
  const estado = useEstadoNomina()
  const navegar = useNavigate({ from: Route.fullPath })
  const seccion = Route.useSearch().seccion ?? 'resumen'

  if (estado.isLoading) {
    return <LineaEsqueleto ancho="w-1/2" />
  }

  if (!estado.data?.contratada) {
    return (
      <div className="flex max-w-[900px] flex-col gap-4">
        <EncabezadoPantalla titulo="Nómina" />
        <EstadoVacio
          variante="primera-vez"
          mensaje="La nómina es un complemento: calcula la TSS, el ISR y las prestaciones, y genera los archivos de la TSS y del banco. Para encenderla, hable con Agentical Group."
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Nómina"
        descripcion="La plataforma calcula y genera los archivos; usted los sube a la TSS con su clave y presenta el IR-3 que le propone la DGII."
      />

      <nav aria-label="Secciones de la nómina" className="border-b border-gris-300">
        <ul className="flex flex-wrap gap-1">
          {SECCIONES.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                aria-current={seccion === s.id ? 'page' : undefined}
                onClick={() => void navegar({ search: { seccion: s.id === 'resumen' ? undefined : s.id }, replace: true })}
                className={cx(
                  'inline-flex h-[var(--alto-control)] items-center border-b-2 px-3 text-cuerpo',
                  seccion === s.id ? 'border-marca font-medium text-marca' : 'border-transparent text-gris-600 hover:text-gris-900',
                )}
              >
                {s.etiqueta}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {seccion === 'resumen' ? <ResumenNomina /> : null}
      {seccion === 'empleados' ? <EmpleadosNomina /> : null}
      {seccion === 'periodos' ? <PeriodosNomina /> : null}
      {seccion === 'prestamos' ? <PrestamosNomina /> : null}
      {seccion === 'archivos' ? <ArchivosSeccion /> : null}
      {seccion === 'obligaciones' ? <ObligacionesNomina /> : null}
      {seccion === 'reportes' ? <ReportesNomina /> : null}
      {seccion === 'contabilidad' ? <ContabilidadNomina /> : null}
      {seccion === 'liquidacion' ? <LiquidacionNomina /> : null}
      {seccion === 'simulador' ? <SimuladorNomina /> : null}
      {seccion === 'portal' ? <SolicitudesPortalNomina /> : null}
      {seccion === 'empresa' ? <EmpresaNominaSeccion /> : null}
      {seccion === 'licencias' ? <LicenciasNomina /> : null}
      {seccion === 'asistencia' ? <TiempoNomina /> : null}
      {seccion === 'caja' ? <CajaNomina /> : null}
    </div>
  )
}
